import { Line } from 'rc-progress';
import { capitalize } from 'lodash';
import { PlanDetailsType } from 'src/types/types';

type SubscriptionUsageSectionProps = {
  subscription: any;
  planDetails: PlanDetailsType | null;
  renewalDate: string;
};

export const SubscriptionUsageSection = ({
  subscription,
  planDetails,
  renewalDate,
}: SubscriptionUsageSectionProps) => {
  // Use the subscription-specific usage from planDetails.subscription
  const quota = planDetails?.subscription?.tickets_quota || planDetails?.monthly_quota || 0;
  const used = planDetails?.subscription?.tickets_used || 0;
  const remaining = planDetails?.subscription?.tickets_remaining || Math.max(0, quota - used);
  const percentage = quota > 0 ? (used / quota) * 100 : 0;

  // Email usage from subscription
  const emailQuota =
    planDetails?.subscription?.emails_quota || planDetails?.monthly_email_sent_quota || 0;
  const emailsUsed = planDetails?.subscription?.emails_used || 0;
  const emailPercentage = emailQuota > 0 ? (emailsUsed / emailQuota) * 100 : 0;

  const formatPlanName = (billingType: string) => {
    if (billingType === 'featured') return 'Professional';
    if (billingType.startsWith('enterprise-')) {
      const num = billingType.split('-')[1];
      return `Enterprise ${Number(num).toLocaleString()}`;
    }
    return capitalize(billingType);
  };

  const getColorForPercentage = (pct: number) => {
    if (pct >= 95) return '#FF5252';
    if (pct > 80) return '#FFA726';
    if (pct >= 60) return '#42A5F5';
    return '#66BB6A';
  };

  return (
    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e0e0e0' }}>
      <div className="mb-2">
        <span className="text-sm text-600">Plan:</span>{' '}
        <span className="font-semibold">{formatPlanName(subscription?.billing_type)}</span>
      </div>
      <div className="mb-2">
        <span className="text-sm text-600">Monthly quota:</span>{' '}
        <span className="font-semibold">{quota.toLocaleString()} tickets</span>
      </div>
      <div className="mb-3">
        <span className="text-sm text-600">Renews:</span>{' '}
        <span className="font-semibold">{renewalDate}</span>
      </div>

      <div className="mb-2">
        <div className="flex justify-content-between mb-1">
          <span className="text-sm">Tickets used this month: {used.toLocaleString()}</span>
          <span className="text-sm font-bold" style={{ color: getColorForPercentage(percentage) }}>
            {Math.round(percentage)}%
          </span>
        </div>
        <Line
          percent={percentage}
          strokeWidth={3}
          trailColor="#f0f0f0"
          strokeColor={getColorForPercentage(percentage)}
          strokeLinecap="round"
        />
        <div className="mt-1 text-sm text-600">
          Remaining this month: {remaining.toLocaleString()}
        </div>
      </div>

      {emailQuota > 0 && (
        <div className="mt-3">
          <div className="text-sm text-600 mb-1">
            Emails used: {emailsUsed.toLocaleString()} / {emailQuota.toLocaleString()} (
            {Math.round(emailPercentage)}%)
          </div>
        </div>
      )}
    </div>
  );
};
